import { api, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import type { Role } from '../../auth/session';

export type Member = Schemas['Member'];
export type Invitation = Schemas['Invitation'];
export type IssuedInvitation = Schemas['IssuedInvitation'];

/** What each role can do, as the API and database enforce it. */
export const ROLE_INFO: Record<Role, [string, string]> = {
  owner: ['Owner', 'Everything, including the team, other owners and reopening locked periods'],
  admin: ['Admin', 'Everything except changing owners: company details, the team and period locks'],
  accountant: ['Accountant', 'Books, reports, reconciliation and the chart of accounts'],
  staff: ['Staff', 'Create and send invoices, record payments and bills'],
  read_only: ['Read only', 'View documents and reports'],
  employee: ['Employee', 'Only their own expenses: upload receipts and submit them for approval'],
};
export const ROLE_ORDER: Role[] = ['owner', 'admin', 'accountant', 'staff', 'read_only', 'employee'];

/** The roles the caller may hand out: only an owner makes owners. */
export const assignableRoles = (mine: Role): Role[] => ROLE_ORDER.filter((r) => r !== 'owner' || mine === 'owner');

/** Whether the caller can change this member at all. Owners are changed only by owners. */
export const canManage = (mine: Role, target: Member) => (mine === 'owner' || mine === 'admin') && (target.role !== 'owner' || mine === 'owner');

export const useMembers = () => useApiQuery(['team', 'members'], async () => ((await unwrap(api.GET('/v1/members'))).data ?? []) as Member[]);
export const useInvitations = (enabled: boolean) =>
  useApiQuery(['team', 'invitations'], async () => ((await unwrap(api.GET('/v1/invitations'))).data ?? []) as Invitation[], { enabled });

export const displayName = (m: Pick<Member, 'name' | 'email'>) => m.name?.trim() || (m.email ?? '').split('@')[0] || 'Unnamed';

/** A short "Oct 16, 2026" from an ISO timestamp, in the viewer's time zone. */
export const fmtStamp = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso)) : '';

/** Copies text; false when the browser refuses (the caller then shows the text to copy by hand). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
